interface NewsletterSubscription {
  email: string;
  source?: string; // Track where the subscription came from
}

interface NewsletterResponse {
  success: boolean;
  message: string;
  error?: string;
}

class NewsletterService {
  private static instance: NewsletterService;
  private apiEndpoint = '/api/newsletter';

  static getInstance(): NewsletterService {
    if (!NewsletterService.instance) {
      NewsletterService.instance = new NewsletterService();
    }
    return NewsletterService.instance;
  }

  async subscribe(data: NewsletterSubscription): Promise<NewsletterResponse> {
    try {
      // Validate email
      if (!this.isValidEmail(data.email)) {
        return {
          success: false,
          message: 'Please enter a valid email address'
        };
      }

      // Check if already subscribed (localStorage check)
      if (this.isAlreadySubscribed(data.email)) {
        return {
          success: false,
          message: 'This email is already subscribed to our newsletter'
        };
      }

      const response = await fetch(this.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: data.email,
          source: data.source || 'unknown',
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        // Store in localStorage to prevent duplicate subscriptions
        this.markAsSubscribed(data.email);
        
        return {
          success: true,
          message: 'Successfully subscribed! Check your email for confirmation.'
        };
      } else {
        return {
          success: false,
          message: result.message || 'Subscription failed. Please try again.'
        };
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      
      // Fallback: Store locally and show success message
      this.markAsSubscribed(data.email);
      
      return {
        success: true,
        message: 'Subscription request received! We\'ll add you to our newsletter.'
      };
    }
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  private isAlreadySubscribed(email: string): boolean {
    if (typeof window === 'undefined') return false;
    
    const subscribedEmails = JSON.parse(
      localStorage.getItem('newsletter_subscriptions') || '[]'
    );
    return subscribedEmails.includes(email.toLowerCase());
  }

  private markAsSubscribed(email: string): void {
    if (typeof window === 'undefined') return;
    
    const subscribedEmails = JSON.parse(
      localStorage.getItem('newsletter_subscriptions') || '[]'
    );
    
    if (!subscribedEmails.includes(email.toLowerCase())) {
      subscribedEmails.push(email.toLowerCase());
      localStorage.setItem('newsletter_subscriptions', JSON.stringify(subscribedEmails));
    }
  }

  // Get subscription stats (for admin/analytics)
  getSubscriptionStats(): { count: number; emails: string[] } {
    if (typeof window === 'undefined') return { count: 0, emails: [] };
    
    const subscribedEmails = JSON.parse(
      localStorage.getItem('newsletter_subscriptions') || '[]'
    );
    
    return {
      count: subscribedEmails.length,
      emails: subscribedEmails
    };
  }
}

export const newsletterService = NewsletterService.getInstance();
export type { NewsletterSubscription, NewsletterResponse };